<template>
    <div :class="{
        'filter-container': true,
        'filter-closed': !showDrawer,
        'mobile-hidden': isBuystackFlyoutOpened && showDrawer
    }">
        <div class="filter" :key="props.filtersKey">
            <div class="filter-header">
                <span class="title-m">{{ `${sortOptions?.length > 0 ? 'Sort & Filters' : 'Filters'}` }}</span>
                <a href="#"><CloseIcon id="close-drawer" alt="" @click="closeDrawer()" /></a>
            </div>
            <div v-if="activeFilters.length > 0" class="filters-applied">
                <div class="applied-filters filter-badges">
                    <label v-for="(filter, i) in activeFilters" :key="i" :aria-label="filter.facet.label" :id="`${filter.facet.field}_${filter.value.value ?? filter.value.low}_badge`"
                        @click="addOrRemoveFilter({ ...filter, active: false })">
                        <span class="applied-filter" v-if="filter.facet.type == 'slider'">
                            {{ `${filter.facet.field == 'price' ? '$' : ''}${filter.value.low}-${filter.facet.field == 'price' ? '$' : ''}${filter.value.high}`}}
                        </span>
                        <span class="applied-filter" v-else-if="filter.facet.field == 'rating'">
                            <FiveStars v-if="filter.value.low == 5" src="@icons/5_star.svg" alt="5 stars" class="stars" />
                            <FourStars v-else-if="filter.value.low == 4" src="@icons/4_star.svg" alt="4 stars" class="stars" />
                            <ThreeStars v-else-if="filter.value.low == 3" src="@icons/3_star.svg" alt="3 stars" class="stars" />
                            <TwoStars v-else-if="filter.value.low == 2" src="@icons/2_star.svg" alt="2 stars" class="stars" />
                            <OneStar v-else-if="filter.value.low == 1" src="@icons/1_star.svg" alt="1 star" class="stars" />
                        </span>
                        <span class="applied-filter" v-else-if="filter.facet.field == 'ss_category_hierarchy' && contentPage">
                            {{ title(filter.value.label) }}
                        </span>
                        <span class="applied-filter" v-else>
                            {{ filter.value.label }}
                        </span>
                    </label>
                </div>
                <a class="clear-btn" id="clear_all" @click.prevent="removeAllFilters()">Clear All</a>
            </div>
            <p class="filter-no-filters" v-if="allFacets.length == 0">
                No Filters Available
            </p>
            <div class="filter-accordion-container">

                <details v-if="sortOptions?.length > 0"
                    class="filter-section desktop-hidden" id="sort_by" v-bind="{'open': accordionState?.['sort_by']}" @toggle.prevent="toggleAccordion('sort_by',$event)">
                    <summary class="filter-title" tabindex="0">Sort By</summary>
                    <form class="filter-content">
                        <label v-for="(option, index) in props.sortOptions" :key="index" :for="option.label">
                            <input type="radio" :id="option.label" name="sort" @change="changeSort(option)" :checked="sortOption.label == option.label" :value="option.label">{{ option.label }}
                        </label>
                    </form>
                </details>

                <Facet
                    v-for="facet in allFacets"
                    :id="`${facet.field}_facet`"
                    :ref="facet.field"
                    :key="facet.field"
                    :facet="facet ? facet : {relatedActiveFilters:[]}"
                    @addOrRemoveFilter="addOrRemoveFilter"
                    v-bind="facet.attrs"
                    :content-facet="contentPage"

                />
            </div>
            <div v-show="activeFilters.length > 0" class="filter-bottom">
                <a href="#" class="clear-all-button" @click="removeAllFilters()">Clear All</a>
                <RothcoCta id="see-results" class="primary" @click="closeDrawer()">See Results {{ numResults }}</RothcoCta>
            </div>
        </div>
    </div>
    <div @click="closeDrawer" class="overlay" style="z-index: 1000" aria-hidden="true"></div>
</template>
<script setup lang="ts">
    import { ref, computed, watch, onMounted, type PropType } from "vue";
    import { router } from "@inertiajs/vue3";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    import Facet from "@/components/SearchSpringFacet.vue";
    import { type SearchSpringFacet } from "@interfaces/SearchSpringFacet";
    import { storeToRefs } from "pinia";
    import { useAccordionState } from "@/store/accordionState";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { useSettingsStore } from "@/store/settings";
    import CloseIcon from "@icons/closeIcon.svg";
    import FiveStars from "@icons/5_star.svg";
    import FourStars from "@icons/4_star.svg";
    import ThreeStars from "@icons/3_star.svg";
    import TwoStars from "@icons/2_star.svg";
    import OneStar from "@icons/1_star.svg";

    let showDrawer = defineModel("showDrawer", {
        type: Boolean,
        default: false
    });

    interface Filter {
        facet: object;
        value: object;
    }
    let activeFilters = defineModel<Filter[]>("activeFilters", {
        type: Array,
        default() {
            return [];
        }
    });

    let sortOption = defineModel("sortOption", {
        type: Object,
        default: {}
    });

    let {
        isBuystackFlyoutOpened,
    } = storeToRefs(useSettingsStore());

    const changeSort = (option)=>{
        sortOption.value = option;
    }

    let props = defineProps({
        contentPage: {
            type: Boolean,
            default: false
        },
        facets: {
            type: Array as PropType<SearchSpringFacet[]>,
            required: true
        },
        sortOptions: {
            type: Array,
            required: true,
            default(){
                return []
            }
        },
        numResults: {
            type: Number,
            default: 0
        },
        isDealer: {
            type: Boolean,
            default: false
        },
        forceFacetDisplay: {
            type: Boolean,
            default: false
        },
        filtersKey: {
            type: String,
            default: 'filters-key'
        }
    });

    const facetsToExclude = ['is_new', 'b2b_product', 'advertising_restricted'];

    let allFacets = computed(() => {
        return props.facets.filter(facet => {
            if(facet.values?.length == 1){
                return facet.values[0].count != props.numResults || props.forceFacetDisplay;
            }

            if(facet.values?.length == 0){
                let hasActiveFilter = activeFilters.value.find((filter)=>(filter.facet.field == facet.field));
                if(['ss_category_hierarchy'].includes(facet.field) && hasActiveFilter){
                    return true;
                }
                else if(['price'].includes(facet.field) && !props.isDealer){
                    return true;
                }
                return false;
            }

            if(facetsToExclude.includes(facet.field)){
                return false;
            }
            return true;
        }).map((facet, index) => {
            return {
                ...facet,
                relatedActiveFilters: activeFilters.value.filter(
                    (activeFilter) => activeFilter.facet.field == facet.field
                ),
                attrs: index < 5 ? {
                    open: true
                } : {}
            };
        });
    });




    function closeDrawer() {
        showDrawer.value = false;
    }

    function removeAllFilters() {
        const url = new URL(window.location.href);
        const urlSearchParamKeys = [...new URLSearchParams(window.location.search).keys()].filter(key => key.includes("ss["));
        if(urlSearchParamKeys.length > 0){
            for(let key of urlSearchParamKeys){
                url.searchParams.delete(key);
            }
            router.get(url.pathname + url.search, {}, { replace: true, preserveState: true, preserveScroll:true, onFinish: ()=>{
                activeFilters.value = [];
            } });
        }else{
            activeFilters.value = [];
        }
    }

    function addOrRemoveFilter(filter) {
        let { facet, value, active } = filter;
        if (active) {
            if (facet.multiple == "single" || facet.type == "slider" || facet.field == 'rating') {
                activeFilters.value = [
                    ...[
                        ...activeFilters.value.filter(
                            (obj) => !(obj.facet.field == facet.field)
                        )
                    ],
                    { facet, value }
                ];
            } else {
                activeFilters.value = [
                    ...activeFilters.value,
                    { facet, value }
                ];
            }
        } else {
            const url = new URL(window.location.href);
            if(url.searchParams.has(`ss[${filter.facet.field}]`)){
                url.searchParams.delete(`ss[${filter.facet.field}]`);
                router.get(url.pathname + url.search, {}, { replace: true, preserveState: true, preserveScroll: true, onFinish: ()=>{
                    removeFilterFromActiveFilters(filter);
                } });
            }else{
                removeFilterFromActiveFilters(filter);
            }
        }
    }

    const removeFilterFromActiveFilters = (filter)=>{
        let { facet, value } = filter;
        activeFilters.value = [
            ...activeFilters.value.filter(
                (obj) =>
                    !(
                        obj.facet.field == facet.field &&
                        (obj.value.value == value.value 
                            || ((facet.type == 'slider' || facet.field == 'rating') && obj.value.low == value.low && obj.value.high == value.high))
                    )
            )
        ];
    }
    
    const { accordionState } = storeToRefs(useAccordionState());
    const { toggleAccordion } = useAccordionState();

    function title(name){
        return name.split('-').map((word)=>{
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    }

    let breakpoints;
    let smallerThanLaptop;
    onMounted(()=>{
        breakpoints = useRothcoBreakpoints();
        smallerThanLaptop = breakpoints.smallerOrEqual('laptop');

        if(breakpoints.smallerOrEqual('laptop').value){
            showDrawer.value = false;
        }else{
            const urlParams = new URLSearchParams(window.location.search);
            showDrawer.value = allFacets.value.length > 0 && allFacets.value.some(facet => facet.values.length > 0);
        }

        watch(smallerThanLaptop, (newVal, oldVal)=>{
            if(newVal && !oldVal){
                showDrawer.value = false;
            }
        })
    })
</script>
